import { useEffect, useState } from "react";
import {
  adminDashboardSchema,
  type AdminDashboardData,
} from "@voice/api-contract";
import type { AccountTab } from "./portal-navigation";
import { ServiceStatusPanel } from "./ServiceStatus";

type Actions = {
  onNavigate: (section: string) => void;
  onAccount: (id: string, tab?: AccountTab) => void;
};
type DashboardApi = <T>(
  path: string,
  options?: { signal?: AbortSignal },
) => Promise<T>;

export const dashboardQueues = [
  {
    key: "ordersNeedingAttention",
    label: "Service orders need attention",
    section: "orders",
  },
  {
    key: "portsNeedingInfo",
    label: "Port requests need information",
    section: "ports",
  },
  { key: "newInquiries", label: "New service inquiries", section: "inbox" },
  { key: "openMessages", label: "Open account messages", section: "messages" },
] as const;

export function setupTasks(account: AdminDashboardData["setups"][number]) {
  return [
    ...(!account.has_contact ? ["Contact details missing"] : []),
    ...(!account.has_address ? ["Service address missing"] : []),
    ...(!account.has_number ? ["No number linked"] : []),
    ...(!account.has_equipment ? ["No equipment recorded"] : []),
  ];
}

export function setupNextStep(account: AdminDashboardData["setups"][number]): {
  label: string;
  tab: AccountTab;
} {
  if (!account.has_contact)
    return { label: "Add contact details", tab: "overview" };
  if (!account.has_address)
    return { label: "Add service address", tab: "overview" };
  if (!account.has_number) return { label: "Add a number", tab: "numbers" };
  if (!account.has_equipment) return { label: "Add equipment", tab: "devices" };
  return { label: "Review activation", tab: "overview" };
}

export function DashboardContent({
  data,
  onNavigate,
  onAccount,
  refreshing = false,
}: Actions & {
  data: AdminDashboardData;
  refreshing?: boolean;
}) {
  const attention = dashboardQueues.filter(
    (queue) => data.queues[queue.key] > 0,
  );
  return (
    <div className="cr-dashboard" aria-busy={refreshing}>
      <div className="cr-dashboard-start">
        <p className="cr-form-note">
          Updated{" "}
          <time dateTime={data.generatedAt}>
            {new Date(data.generatedAt).toLocaleString([], {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </time>
          . Portal records, not live call monitoring.
        </p>
        <div className="cr-dashboard-account-actions">
          {refreshing && <span role="status">Refreshing dashboard…</span>}
          <button className="cr-button" onClick={() => onNavigate("customers")}>
            Manage customers
          </button>
        </div>
      </div>
      <section
        className={
          attention.length ? "cr-dashboard-attention" : "cr-dashboard-clear"
        }
        aria-labelledby="dashboard-attention"
      >
        <div className="cr-dashboard-section-heading">
          <h2 id="dashboard-attention">
            {attention.length ? "Needs attention" : "Work queues"}
          </h2>
        </div>
        {attention.length ? (
          <ul className="cr-dashboard-queue">
            {attention.map((queue) => (
              <li key={queue.key}>
                <button onClick={() => onNavigate(queue.section)}>
                  <strong>{data.queues[queue.key].toLocaleString()}</strong>
                  <span>{queue.label}</span>
                  <span className="cr-dashboard-open">Review</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>
            No new inquiries, open account messages, port requests needing
            information, or flagged service orders.
          </p>
        )}
        <p className="cr-form-note">
          Customer setup is listed below. A clear work queue does not confirm
          that phone service is working.
        </p>
      </section>
      <section aria-labelledby="dashboard-setup">
        <div className="cr-dashboard-section-heading">
          <div>
            <h2 id="dashboard-setup">Customer setup</h2>
            <p>
              {data.queues.setups.toLocaleString()} accounts pending activation.
            </p>
          </div>
          <button
            className="cr-text-button"
            onClick={() => onNavigate("customers")}
          >
            All customers
          </button>
        </div>
        {data.setups.length ? (
          <ul className="cr-dashboard-setups">
            {data.setups.map((account) => {
              const tasks = setupTasks(account);
              const next = setupNextStep(account);
              return (
                <li key={account.id}>
                  <div>
                    <button
                      className="cr-text-button cr-dashboard-account"
                      onClick={() => onAccount(account.id)}
                    >
                      {account.name}
                    </button>
                    <p className="cr-form-note">
                      {account.account_number} ·{" "}
                      {account.kind === "enterprise"
                        ? "Enterprise · MozartCX"
                        : account.kind === "commercial"
                          ? "Commercial"
                          : "Residential"}
                      {account.billing_mode === "complimentary"
                        ? " · No charge"
                        : ""}
                    </p>
                  </div>
                  <div>
                    <p>
                      {tasks.length
                        ? tasks.join(" · ")
                        : "Setup records entered. Review activation and emergency calling."}
                    </p>
                    <div className="cr-dashboard-account-actions">
                      <button
                        className="cr-text-button"
                        onClick={() => onAccount(account.id, next.tab)}
                      >
                        {next.label}
                      </button>
                      {!account.has_number && next.tab !== "numbers" && (
                        <button
                          className="cr-text-button"
                          onClick={() => onAccount(account.id, "numbers")}
                        >
                          Add a number
                        </button>
                      )}
                      {!account.has_equipment && next.tab !== "devices" && (
                        <button
                          className="cr-text-button"
                          onClick={() => onAccount(account.id, "devices")}
                        >
                          Add equipment
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p>No customer accounts are pending activation.</p>
        )}
        {data.queues.setups > data.setups.length && (
          <p className="cr-form-note">
            Showing the {data.setups.length} oldest setups of{" "}
            {data.queues.setups.toLocaleString()}. Open Customers to browse
            customer records.
          </p>
        )}
      </section>
      <section
        className="cr-dashboard-records"
        aria-labelledby="dashboard-records"
      >
        <h2 id="dashboard-records">Account records</h2>
        <dl>
          <div>
            <dt>Customers</dt>
            <dd>{data.totals.customers.toLocaleString()}</dd>
            <dd className="cr-dashboard-record-detail">
              {data.totals.residential.toLocaleString()} residential ·{" "}
              {data.totals.commercial.toLocaleString()} commercial ·{" "}
              {data.totals.enterprise.toLocaleString()} enterprise ·{" "}
              {data.totals.complimentary.toLocaleString()} no charge
            </dd>
          </div>
          <div>
            <dt>Numbers linked to customers</dt>
            <dd>{data.totals.linkedNumbers.toLocaleString()}</dd>
            <dd className="cr-dashboard-record-detail">
              <button
                className="cr-text-button"
                onClick={() => onNavigate("numbers")}
              >
                Open Numbers and available pool
              </button>
            </dd>
          </div>
          <div>
            <dt>Equipment records</dt>
            <dd>{data.totals.equipment.toLocaleString()}</dd>
            <dd className="cr-dashboard-record-detail">
              <button
                className="cr-text-button"
                onClick={() => onNavigate("devices")}
              >
                Open Equipment
              </button>
            </dd>
          </div>
        </dl>
        <p className="cr-form-note">
          Linked numbers exclude released numbers. Equipment includes stored
          inventory and retired devices; this is not an online-device count.
        </p>
      </section>
      <section aria-labelledby="dashboard-activity">
        <h2 id="dashboard-activity">Recent activity</h2>
        {data.activity.length ? (
          <ol className="cr-dashboard-activity">
            {data.activity.map((event) => (
              <li key={event.id}>
                <div>
                  <p>{event.summary}</p>
                  {event.account_id && (
                    <button
                      className="cr-text-button"
                      onClick={() => onAccount(event.account_id!)}
                    >
                      {event.account_name || "Open account"}
                    </button>
                  )}
                </div>
                <time dateTime={event.occurred_at}>
                  {new Date(event.occurred_at).toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </time>
              </li>
            ))}
          </ol>
        ) : (
          <p>No account or staff activity has been recorded yet.</p>
        )}
      </section>
    </div>
  );
}

export function AdminDashboard({
  api,
  refreshVersion = 0,
  ...actions
}: Actions & { api: DashboardApi; refreshVersion?: number }) {
  const [snapshot, setSnapshot] = useState<{
    api: DashboardApi;
    data: AdminDashboardData;
  } | null>(null);
  const data = snapshot?.api === api ? snapshot.data : null;
  const [error, setError] = useState<"" | "failed" | "timeout">("");
  const [refreshing, setRefreshing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    let settled = false;
    const controller = new AbortController();
    setError("");
    setRefreshing(true);
    const deadline = setTimeout(() => {
      if (!current || settled) return;
      settled = true;
      controller.abort();
      setError("timeout");
      setRefreshing(false);
    }, 10_000);
    void Promise.resolve()
      .then(() =>
        api<unknown>("/admin/dashboard", { signal: controller.signal }),
      )
      .then((result) => {
        if (!current || settled) return;
        const parsed = adminDashboardSchema.parse(result);
        settled = true;
        clearTimeout(deadline);
        setSnapshot({ api, data: parsed });
        setRefreshing(false);
      })
      .catch(() => {
        if (!current || settled) return;
        settled = true;
        clearTimeout(deadline);
        setError("failed");
        setRefreshing(false);
      });
    return () => {
      current = false;
      settled = true;
      clearTimeout(deadline);
      controller.abort();
    };
  }, [api, attempt, refreshVersion]);
  const retry = error ? (
    <section className="cr-notice error">
      <p role="alert">
        {error === "timeout"
          ? "The dashboard is taking too long to respond."
          : data
            ? "The dashboard couldn’t refresh."
            : "The dashboard could not load."}{" "}
        {data
          ? "Showing the last successful update below."
          : "Try again or open a section from the menu."}
      </p>
      <button
        className="cr-button secondary"
        onClick={() => setAttempt((value) => value + 1)}
      >
        Try dashboard again
      </button>
    </section>
  ) : null;
  if (!data)
    return (
      retry ?? <p role="status">Loading customer setup and work queues…</p>
    );
  return (
    <>
      <ServiceStatusPanel
        api={api}
        refreshVersion={refreshVersion}
        summary
        onViewStatus={() => actions.onNavigate("service-status")}
      />
      {retry}
      <DashboardContent data={data} {...actions} refreshing={refreshing} />
    </>
  );
}
